import React, { useState, useEffect, useCallback } from 'react';
import AppLayout from '../components/layout/AppLayout';
import { getDeletedTickets, restoreTicket } from '../services/api';
import { format } from 'date-fns';
import {
  Search, Trash2, RotateCcw, ChevronLeft, ChevronRight, Building2, Inbox,
} from 'lucide-react';

const LIMIT = 15;

const PRIORITY_META = {
  critical: { label: 'P1', color: '#dc2626' },
  high:     { label: 'P2', color: '#ea580c' },
  medium:   { label: 'P3', color: '#ca8a04' },
  low:      { label: 'P4', color: '#16a34a' },
};

// The Recycle Bin. Deleting a ticket only stamps deleted_at, so everything here
// is fully recoverable — the page is deliberately read-only apart from Restore.
export default function DeletedTicketsPage() {
  const [tickets, setTickets] = useState([]);
  const [total,   setTotal]   = useState(0);
  const [loading, setLoading] = useState(true);
  const [page,    setPage]    = useState(1);
  const [search,  setSearch]  = useState('');
  const [restoringId, setRestoringId] = useState(null);
  const [notice,  setNotice]  = useState('');
  const [error,   setError]   = useState('');

  const fetchDeleted = useCallback(() => {
    setLoading(true);
    const params = { page, limit: LIMIT };
    if (search) params.search = search;
    getDeletedTickets(params)
      .then(res => { setTickets(res.data.tickets || []); setTotal(res.data.total || 0); })
      .catch(() => setError('Could not load deleted tickets.'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchDeleted(); }, [fetchDeleted]);

  // Debounce the search box so typing doesn't fire a request per keystroke.
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleRestore = async (ticket) => {
    setRestoringId(ticket.id);
    setError(''); setNotice('');
    try {
      await restoreTicket(ticket.id);
      setNotice(`${ticket.ticket_number} restored — it is back in All Tickets.`);
      fetchDeleted();
    } catch {
      setError(`Could not restore ${ticket.ticket_number}.`);
    } finally {
      setRestoringId(null);
    }
  };

  const totalPages = Math.ceil(total / LIMIT) || 1;

  return (
    <AppLayout pageTitle="Deleted Tickets">
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16,
        padding: '10px 14px', borderRadius: 10,
        background: '#fef2f2', border: '1px solid #fecaca', fontSize: 13,
      }}>
        <Trash2 size={15} color="#dc2626" />
        <span style={{ color: '#991b1b' }}>
          Tickets here are hidden from lists, reports and SLA, but nothing has been
          permanently erased. Restore puts a ticket back exactly as it was.
        </span>
      </div>

      {notice && <div className="alert alert-success" style={{ marginBottom: 14 }}>{notice}</div>}
      {error  && <div className="alert alert-error"   style={{ marginBottom: 14 }}>{error}</div>}

      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ position: 'relative', maxWidth: 380 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input
            type="text"
            className="form-control"
            style={{ width: '100%', paddingLeft: 32 }}
            placeholder="Search deleted tickets…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
        </div>
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading…</div>
        ) : tickets.length === 0 ? (
          <div style={{ padding: '56px 20px', textAlign: 'center', color: 'var(--text-muted)' }}>
            <Inbox size={34} style={{ opacity: 0.4 }} />
            <div style={{ marginTop: 12, fontSize: 14, fontWeight: 600 }}>Recycle Bin is empty</div>
            <div style={{ marginTop: 4, fontSize: 12.5 }}>
              {search ? 'No deleted tickets match that search.' : 'Deleted tickets will appear here.'}
            </div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--bg-subtle, #f8fafc)', textAlign: 'left' }}>
                  {['Ticket #', 'Subject', 'Requester', 'Company', 'Priority', 'Deleted By', 'Deleted On', ''].map(h => (
                    <th key={h} style={{ padding: '11px 14px', fontSize: 11, fontWeight: 700, letterSpacing: '0.4px', textTransform: 'uppercase', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tickets.map(t => {
                  const pm = PRIORITY_META[t.priority] || PRIORITY_META.medium;
                  return (
                    <tr key={t.id} style={{ borderTop: '1px solid var(--border, #e5e7eb)' }}>
                      <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)' }}>
                          {t.ticket_number}
                        </span>
                      </td>
                      <td style={{ padding: '12px 14px', maxWidth: 320 }}>
                        <div style={{ color: 'var(--text-main)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {t.title}
                        </div>
                        {t.zyra_module && (
                          <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                            {t.zyra_module}{t.category ? ` › ${t.category}` : ''}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: '12px 14px', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                        {t.requestor_name || t.raised_by_name || '—'}
                      </td>
                      <td style={{ padding: '12px 14px', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                        {t.company_name
                          ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><Building2 size={12} />{t.company_name}</span>
                          : '—'}
                      </td>
                      <td style={{ padding: '12px 14px', whiteSpace: 'nowrap' }}>
                        <span style={{ color: pm.color, fontWeight: 700, fontSize: 12 }}>● {pm.label}</span>
                      </td>
                      <td style={{ padding: '12px 14px', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                        {t.deleted_by_name || '—'}
                      </td>
                      <td style={{ padding: '12px 14px', whiteSpace: 'nowrap', color: 'var(--text-muted)' }}>
                        {t.deleted_at ? format(new Date(t.deleted_at), 'MMM d, yyyy HH:mm') : '—'}
                      </td>
                      <td style={{ padding: '12px 14px', whiteSpace: 'nowrap', textAlign: 'right' }}>
                        <button
                          className="btn btn-outline btn-sm"
                          style={{ color: '#0f766e', borderColor: '#5eead4' }}
                          onClick={() => handleRestore(t)}
                          disabled={restoringId === t.id}
                        >
                          <RotateCcw size={13} /> {restoringId === t.id ? 'Restoring…' : 'Restore'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {total > LIMIT && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, fontSize: 13, color: 'var(--text-muted)' }}>
          <span>{total} deleted ticket{total === 1 ? '' : 's'}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft size={14} /> Prev
            </button>
            <span>Page {page} of {totalPages}</span>
            <button className="btn btn-outline btn-sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Next <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
